import React, { useEffect, useState } from 'react';
import { useTranslate } from 'nucleus-text';
import useTheme from '@hooks/useTheme';
import useTimezone from '@hooks/useTimezone';
import { Row } from '@cvent/carina/components/Row';
import { Col } from '@cvent/carina/components/Col';
import TextLink from '@cvent/carina/components/TextLink';
import { injectTestId } from '@cvent/nucleus-test-automation';
import httpLogPageLoadId from '@tools/httpLogPageLoadId';
import { CSSObject } from '@emotion/react';

interface ErrorContentProps {
  requestId?: string;
  testID?: string;
  cventSupportUrl?: string;
}

const useStyles = (): Record<string, CSSObject> => {
  const { font } = useTheme();
  const contrastingColor = font.color.base;
  return {
    container: {
      alignSelf: 'center',
      textAlign: 'center',
      width: 300,
      margin: '0 auto'
    },
    cardContent: {
      width: '100%'
    },
    errorInfoMessage: {
      fontWeight: font.mono.weight.regular,
      color: contrastingColor,
      margin: '0 auto',
      width: 300
    },
    errorRowTitle: {
      fontSize: font.prose.size.xxs,
      fontWeight: font.base.weight.bold,
      color: contrastingColor,
      marginBottom: 4
    },
    errorRowDetails: {
      fontSize: font.prose.size.xs,
      color: contrastingColor
    },
    helpLink: {
      fontSize: font.prose.size.xxs,
      color: font.color.interactive.base,
      textDecoration: 'none'
    }
  };
};

export function ErrorInfoRow({ title, value, testID }): JSX.Element {
  const styling = useStyles();
  return (
    <>
      <h2 css={styling.errorRowTitle} {...injectTestId(testID)}>
        {title}
      </h2>
      <span css={styling.errorRowDetails} {...injectTestId(`${testID}-value`)}>
        {value}
      </span>
    </>
  );
}

function ErrorContent({ requestId, testID, cventSupportUrl }: ErrorContentProps): JSX.Element {
  const [hydrated, setHydrated] = useState(false);
  const [time] = useState(new Date());
  const { date } = useTimezone();
  const styling = useStyles();
  const { translate } = useTranslate();

  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div css={styling.container} {...injectTestId(testID)}>
      <div css={styling.cardContent}>
        <Row justifyContent="center">
          <Col>
            <span {...injectTestId(`${testID}-message`)} css={styling.errorInfoMessage}>
              {translate('UnexpectedError-ErrorInfo-Message')}
            </span>
            <ErrorInfoRow
              title={translate('UnexpectedError-ErrorInfo-Time')}
              value={
                hydrated
                  ? date(time, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      timeZoneName: 'short'
                    })
                  : null
              }
              testID={`${testID}-time`}
            />
            {requestId && (
              <ErrorInfoRow
                title={translate('UnexpectedError-ErrorInfo-InstanceId')}
                value={requestId}
                testID={`${testID}-requestId`}
              />
            )}
            <ErrorInfoRow
              title={translate('UnexpectedError-ErrorInfo-PageLoadId')}
              value={hydrated ? httpLogPageLoadId : null}
              testID={`${testID}-pageLoadId`}
            />
            <Row justifyContent="center" margin={{ top: 24 }}>
              <TextLink href={cventSupportUrl} target="_blank" rel="noopener noreferrer">
                <span css={styling.helpLink} {...injectTestId(`${testID}-help-link`)}>
                  {translate('UnexpectedError-ErrorInfo-HelpMe')}
                </span>
              </TextLink>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default ErrorContent;
